import React from 'react';
import type { StatutPlanningRowBase } from '../../../config';
import { statuts } from '../../../config';

/** Helpers de date/format/tri extraits de OperationalPlanningModal.tsx. */

export const DEFAULT_PLANNING_NOM = 'PERSONNELS';

export const PLANNING_GROUPE_KEYS = ['A', 'B', 'C', 'D'] as const;
export type PlanningGroupeKey = (typeof PLANNING_GROUPE_KEYS)[number];

export interface PlanningPersonnelRow {
  id: number;
  nom: string;
  prenom: string;
  grade: string;
  grade_code?: string;
  grade_ordre?: number | null;
  planning_statut_nom?: string | null;
  batiment_id?: number | null;
  planning_weeks_json?: unknown;
  /** Groupe opérationnel A–D (`player_staff.groupe`) */
  groupe?: string | null;
}

/** Normalise la valeur API (A, GR-B, etc.) pour les filtres. */
export function normalizePlanningGroupeValue(
  raw: string | null | undefined
): PlanningGroupeKey | null {
  if (raw == null || String(raw).trim() === '') return null;
  const u = String(raw).trim().toUpperCase();
  if (u === 'A' || u === 'B' || u === 'C' || u === 'D') return u;
  const m = u.match(/^(?:GR[-\s]?|GROUPE[-\s]?)?([ABCD])(?:\b|$)/);
  if (m) return m[1].toUpperCase() as PlanningGroupeKey;
  return null;
}

export function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function addLocalDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return startOfLocalDay(x);
}

export function startOfWeekMonday(d: Date): Date {
  const x = startOfLocalDay(d);
  const dow = x.getDay();
  const daysSinceMonday = (dow + 6) % 7;
  return addLocalDays(x, -daysSinceMonday);
}

export function toLocalDateInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function parseLocalDateInput(iso: string): Date {
  const [y, m, d] = iso.split('-').map((x) => parseInt(x, 10));
  return startOfLocalDay(new Date(y, m - 1, d));
}

export function formatWeekBannerFr(weekStart: Date): string {
  const weekEnd = addLocalDays(weekStart, 6);
  const optsLong: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  };
  const a = weekStart.toLocaleDateString('fr-FR', optsLong);
  const b = weekEnd.toLocaleDateString('fr-FR', optsLong);
  return `Semaine du ${a.charAt(0).toUpperCase() + a.slice(1)} au ${b.charAt(0).toUpperCase() + b.slice(1)}`;
}

export function formatDayHeaderFr(d: Date): { weekday: string; dayMonth: string } {
  return {
    weekday: d.toLocaleDateString('fr-FR', { weekday: 'short' }).replace('.', ''),
    dayMonth: d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }).replace('.', ''),
  };
}

export function sortPlanningPersonnel(rows: PlanningPersonnelRow[]): PlanningPersonnelRow[] {
  return [...rows].sort((x, y) => {
    const ox =
      x.grade_ordre != null && !Number.isNaN(Number(x.grade_ordre)) ? Number(x.grade_ordre) : 999_999;
    const oy =
      y.grade_ordre != null && !Number.isNaN(Number(y.grade_ordre)) ? Number(y.grade_ordre) : 999_999;
    if (ox !== oy) return ox - oy;
    const g = (x.grade || '').localeCompare(y.grade || '', 'fr', { sensitivity: 'base' });
    if (g !== 0) return g;
    const nx = `${x.nom} ${x.prenom}`.trim();
    const ny = `${y.nom} ${y.prenom}`.trim();
    return nx.localeCompare(ny, 'fr', { sensitivity: 'base' });
  });
}

export function sortPlanningLegendRows(rows: StatutPlanningRowBase[]): StatutPlanningRowBase[] {
  return [...rows].sort((a, b) => {
    const pa = a.prio != null ? a.prio : 9999;
    const pb = b.prio != null ? b.prio : 9999;
    if (pa !== pb) return pa - pb;
    return a.nom.localeCompare(b.nom, 'fr');
  });
}

export function resolvePlanningColors(
  planningNom: string | null | undefined,
  serviceLegend: readonly StatutPlanningRowBase[]
): {
  backgroundColor: string;
  textColor: string;
} {
  const u = (planningNom || DEFAULT_PLANNING_NOM).trim().toUpperCase();
  const inSvc = serviceLegend.find((p) => p.nom.toUpperCase() === u);
  if (inSvc) return inSvc.couleurs;
  const s = statuts.statuts_planning.find((p) => p.nom.toUpperCase() === u);
  if (s) return s.couleurs;
  return { backgroundColor: '#f97316', textColor: '#fff' };
}

/** Code court pour l'impression (affiche `code` sinon l'`id` config, comme la légende à l'écran). */
export function planningCodeLabel(
  planningNom: string | null | undefined,
  serviceLegend: readonly StatutPlanningRowBase[]
): string {
  const u = (planningNom || DEFAULT_PLANNING_NOM).trim().toUpperCase();
  const inSvc = serviceLegend.find((p) => p.nom.toUpperCase() === u);
  if (inSvc) {
    const c = inSvc.code?.trim();
    return c || String(inSvc.id);
  }
  const s = statuts.statuts_planning.find((p) => p.nom.toUpperCase() === u);
  if (s) {
    const c = s.code?.trim();
    return c || String(s.id);
  }
  const raw = (planningNom || DEFAULT_PLANNING_NOM).trim();
  return raw.length > 14 ? `${raw.slice(0, 12)}…` : raw;
}

export const printColorAdjustStyle: React.CSSProperties = {
  WebkitPrintColorAdjust: 'exact',
  printColorAdjust: 'exact',
};

/** Contour intérieur double (clair + foncé) pour repérer le jour J sur n'importe quelle couleur de statut. */
export const todayColumnCellBoxShadow =
  'inset 0 0 0 2px #fffbeb, inset 0 0 0 6px #b45309';

/** Légende globale (tous services) : une entrée par libellé `nom`. */
export function dedupePlanningLegendAllServices(): StatutPlanningRowBase[] {
  const byNom = new Map<string, StatutPlanningRowBase>();
  for (const s of statuts.statuts_planning) {
    const k = s.nom.trim().toUpperCase();
    if (!byNom.has(k)) {
      const { service: _omit, ...rest } = s;
      byNom.set(k, rest);
    }
  }
  return sortPlanningLegendRows(Array.from(byNom.values()));
}

export function parsePlanningWeeksJson(raw: unknown): Record<string, string[]> {
  if (raw == null) return {};
  let o: unknown = raw;
  if (typeof raw === 'string') {
    try {
      o = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (typeof o !== 'object' || o === null || Array.isArray(o)) return {};
  const out: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(o as Record<string, unknown>)) {
    if (Array.isArray(v) && v.length === 7) {
      out[k] = v.map((x) => {
        const s = String(x ?? DEFAULT_PLANNING_NOM).trim();
        return s || DEFAULT_PLANNING_NOM;
      });
    }
  }
  return out;
}

/**
 * Libellé planning pour une date locale : jour dans la semaine du lundi,
 * avec repli sur la semaine précédente puis `planning_statut_nom`.
 */
export function getPlanningLabelForLocalDate(
  row: { planning_weeks_json?: unknown; planning_statut_nom?: string | null },
  d: Date = new Date()
): string {
  const weeks = parsePlanningWeeksJson(row.planning_weeks_json);
  const monday = startOfWeekMonday(d);
  const weekKey = toLocalDateInputValue(monday);
  const prevWeekKey = toLocalDateInputValue(addLocalDays(monday, -7));
  const idx = (startOfLocalDay(d).getDay() + 6) % 7;

  const pick = (arr: string[] | undefined): string | null => {
    if (!arr || arr.length !== 7) return null;
    const s = String(arr[idx] ?? '').trim();
    return s || null;
  };

  const fromWeek = pick(weeks[weekKey]) ?? pick(weeks[prevWeekKey]);
  if (fromWeek) return fromWeek;

  const fb =
    (row.planning_statut_nom && String(row.planning_statut_nom).trim()) || DEFAULT_PLANNING_NOM;
  return fb || DEFAULT_PLANNING_NOM;
}

function fill7FromNom(nom: string | null | undefined): string[] {
  const n = (nom && String(nom).trim()) || DEFAULT_PLANNING_NOM;
  return Array.from({ length: 7 }, () => n);
}

/** Jours effectifs : semaine enregistrée → sinon copie semaine précédente → sinon nom × 7. */
export function effectiveWeekDays(
  row: PlanningPersonnelRow,
  weekKey: string,
  prevWeekKey: string
): string[] {
  const weeks = parsePlanningWeeksJson(row.planning_weeks_json);
  if (weeks[weekKey]?.length === 7) return weeks[weekKey];
  if (weeks[prevWeekKey]?.length === 7) return [...weeks[prevWeekKey]];
  return fill7FromNom(row.planning_statut_nom);
}

export const WEEK_DAY_COUNT = 7;
